import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  formatListingAge,
  isEquipmentListingType,
  normalizeManagedStockQuantity,
} from '../../util/listingRules';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  H1,
  H4,
  Modal,
  SecondaryButton,
  PriceDisplay,
} from '../../components';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';
import ContactChevalierForm from '../ContactChevalierForm/ContactChevalierForm';

import css from './OrderPanel.module.css';
import Icons from '../Icons/Icons';
import { handleBidTransaction } from '../../util/ListingPageHelpers';
import { BiddingFormComponent } from './BiddingForm/BiddingForm';
import { useDispatch, useSelector } from 'react-redux';
import { currentUserSelector } from '../../ducks/user.duck';
import {
  formatUnixToDateTime,
  getCountdownParts,
  getListingBids,
  isArrayLength,
  maskUserFullName,
  pad2,
} from '../../util/genericHelpers';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { USD } from '../../util/enums';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';
import {
  bidExistenceSelector,
  bidTransactionSelector,
} from '../../containers/ListingPage/ListingPage.duck';
import { getOfferParams } from '../../util/dataExtractor';
import moment from 'moment';
import { useFavoriteListings } from '../../hooks/useFavoriteListings';
import { useContactChevalierRequest } from '../../hooks/useContactChevalierRequest';
import { getBidUnitLabels } from '../../util/bidPricing';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);
const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);
const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => listing.attributes.state === LISTING_STATE_PUBLISHED;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

/* =========================
   ✅ Auction helpers
   ========================= */
const titleCaseFromSlug = str =>
  typeof str === 'string' ? str.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    offerParams,
  } = props;
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { listingType, unitType } = publicData || {};
  const offerParamsPrice = offerParams?.price / 100;
  const { formatNumber } = intl;
  const offerParamsCurrency = offerParams?.currency;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const priceValue = (
    <PriceDisplay
      className={css.priceValue}
      value={
        offerParamsPrice
          ? formatNumber(offerParamsPrice, {
              style: 'currency',
              currency: offerParamsCurrency,
            })
          : formatMoneyIfSupportedCurrency(price, intl)
      }
    />
  );
  // const priceValue = <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>;
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  const titleValue = isAuthenticated ? priceTitle : undefined;

  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={titleValue}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{
            priceValue: <PriceDisplay className={css.priceValueInCTA} value={formattedPrice} />,
          }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
      </p>
    </div>
  );
};

const PriceMissing = () => (
  <p className={css.error}>
    <FormattedMessage id="OrderPanel.listingPriceMissing" />
  </p>
);
const InvalidCurrency = () => (
  <p className={css.error}>
    <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
  </p>
);
const InvalidPriceVariants = () => (
  <p className={css.error}>
    <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
  </p>
);

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);
  return variantsHaveNames && namesAreUnique;
};

const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const [selectedOrderQuantity, setSelectedOrderQuantity] = useState(null);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  // ✅ countdown tick (only used for auction)
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    hideAuthorInfo = false,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    transaction,
    setIsOfferModalOpen,
    shouldHideMakeAnOfferButton,
    isListingPage,
  } = props;

  const offerParams = getOfferParams(transaction);
  const currentUser = useSelector(currentUserSelector);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const maskCurrentUserName = maskUserFullName(currentUser);
  const routeConfiguration = useRouteConfiguration();
  const listingBids = getListingBids(listing);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { isFavorited, toggleFavorite } = useFavoriteListings();
  const { isBidAlreadyPlaced } = useSelector(bidExistenceSelector);
  const { placeBidInProgress } = useSelector(bidTransactionSelector);
  const {
    closeModal: closeContactModal,
    error: contactError,
    initialValues: contactInitialValues,
    inProgress: contactInProgress,
    isModalOpen: isContactModalOpen,
    openModal: openContactModal,
    submitContactRequest,
    success: contactSuccess,
  } = useContactChevalierRequest({ listing, currentUser });

  const highestBidObject = isArrayLength(listingBids)
    ? listingBids.reduce((maxBid, currentBid) =>
        currentBid.biddingAmount > maxBid.biddingAmount ? currentBid : maxBid
      )
    : null;

  const highestBid = highestBidObject?.biddingAmount || 0;
  const highestBidCurrency = highestBidObject?.biddingCurrency || USD;

  const publicData = listing?.attributes?.publicData || {};
  const listingAgeSummary = formatListingAge(publicData, intl, { maxUnits: 2 });
  const listingId = listing?.id?.uuid;
  const isListingFavorited = listingId ? isFavorited(listingId) : false;
  const favoriteLabel = isListingFavorited
    ? intl.formatMessage({ id: 'Favorites.toggleRemove' })
    : intl.formatMessage({ id: 'Favorites.toggleAdd' });
  const {
    listingType,
    unitType,
    transactionProcessAlias = '',
    priceVariants,
    startTimeInterval,
    isAuction,
    auctionEndUnix,
    reservePrice,
    currentHighestBid,
    minimumBid,
    spirit_type,
    currentAbv,
  } = publicData || {};
  const auctionEndTime = auctionEndUnix ? moment.unix(auctionEndUnix) : null;
  const auctionEnabled = !!isAuction && !!auctionEndUnix;

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation;
  const isEquipmentListing = isEquipmentListingType(listingType);

  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const availableOrderQuantity = normalizeManagedStockQuantity(currentStock);
  const bidUnitLabels = getBidUnitLabels(listing);
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;
  const showProductOrderForm =
    mounted &&
    shouldHavePurchase &&
    !isClosed &&
    (currentStock == null || typeof currentStock === 'number');

  const showInquiryForm = mounted && !isClosed && isInquiry;
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  const showRequestQuoteForm =
    !isListingPage && mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems =
    !isEquipmentListing &&
    [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(listingTypeConfig?.stockType);

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
  };

  const showClosedListingHelpText = listing.id && isClosed;
  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  /* =========================
     ✅ Auction: countdown + money objects
     ========================= */

  useEffect(() => {
    if (!auctionEnabled) return;
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [auctionEnabled]);

  const auctionEndMs = auctionEndUnix ? auctionEndUnix * 1000 : 0;
  const countdown = useMemo(() => getCountdownParts(auctionEndMs), [auctionEndMs, nowTick]);

  const reserveMoney = reservePrice?.currency ? reservePrice : null;

  const highestBidMoney =
    currentHighestBid?.currency && typeof currentHighestBid?.amount === 'number'
      ? currentHighestBid
      : typeof currentHighestBid === 'number'
      ? { amount: currentHighestBid, currency: marketplaceCurrency }
      : null;

  const minimumBidMoney =
    minimumBid?.currency && typeof minimumBid?.amount === 'number'
      ? minimumBid
      : typeof minimumBid === 'number'
      ? { amount: minimumBid, currency: marketplaceCurrency }
      : highestBidMoney
      ? { amount: highestBidMoney.amount + 100, currency: highestBidMoney.currency }
      : reserveMoney
      ? { amount: reserveMoney.amount + 100, currency: reserveMoney.currency }
      : null;

  const reserveMet =
    !!highestBidMoney &&
    !!reserveMoney &&
    highestBidMoney.currency === reserveMoney.currency &&
    highestBidMoney.amount >= reserveMoney.amount;
  const endsLabel = auctionEnabled ? formatUnixToDateTime(auctionEndUnix, intl) : '';

  const shortDescriptionLine = useMemo(() => {
    const bits = [];
    if (listingAgeSummary) bits.push(listingAgeSummary);
    if (spirit_type) bits.push(titleCaseFromSlug(spirit_type));
    if (currentAbv != null && currentAbv !== '') bits.push(`${currentAbv}% ABV`);
    return bits.join(' • ');
  }, [listingAgeSummary, spirit_type, currentAbv]);

  return (
    <div className={classes}>
      {/* <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      ></ModalInMobile> */}
      <div className={css.modalHeading}>
        <H4 className={titleClasses}>
          {title}
          <button
            type="button"
            className={classNames(css.favorite, isListingFavorited ? css.favoriteActive : null)}
            aria-pressed={isListingFavorited}
            aria-label={favoriteLabel}
            onClick={() => listingId && toggleFavorite(listingId)}
          >
            <Icons name={isListingFavorited ? 'favorite' : 'unfavorite'} />
          </button>
        </H4>
      </div>

      {showListingImage ? (
        <div className={css.orderHeading}>
          {titleDesktop ? (
            <H4 className={titleClasses}>
              {titleDesktop}{' '}
              <button
                type="button"
                className={classNames(css.favorite, isListingFavorited ? css.favoriteActive : null)}
                aria-pressed={isListingFavorited}
                aria-label={favoriteLabel}
                onClick={() => listingId && toggleFavorite(listingId)}
              >
                <Icons name={isListingFavorited ? 'favorite' : 'unfavorite'} />
              </button>
            </H4>
          ) : (
            <H4 className={titleClasses}>
              {title}
              <button
                type="button"
                className={classNames(css.favorite, isListingFavorited ? css.favoriteActive : null)}
                aria-pressed={isListingFavorited}
                aria-label={favoriteLabel}
                onClick={() => listingId && toggleFavorite(listingId)}
              >
                <Icons name={isListingFavorited ? 'favorite' : 'unfavorite'} />
              </button>
            </H4>
          )}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>
      ) : null}

      <div className={css.liveAuctionSec}>
        {auctionEnabled && isListingPage ? (
          <span className={css.liveAuction}>
            <Icons name="signal" />
            <FormattedMessage id="OrderPanel.auction.live" defaultMessage="Live Timed Sale" />
          </span>
        ) : null}

        {/* <span className={css.verifiedAuthentic}>
          <Icons name="verifyOutline" />
          <FormattedMessage id="OrderPanel.verified" defaultMessage="Verified Authentic" />
        </span> */}
      </div>

      {shortDescriptionLine ? <div className={css.description}>{shortDescriptionLine}</div> : null}

      <PriceMaybe
        price={price}
        publicData={publicData}
        validListingTypes={validListingTypes}
        intl={intl}
        marketplaceCurrency={marketplaceCurrency}
        offerParams={offerParams}
      />
      {!isEquipmentListing &&
      shouldHavePurchase &&
      (availableOrderQuantity != null || Number.isInteger(selectedOrderQuantity)) ? (
        <div className={css.quantitySummaryGroup}>
          {availableOrderQuantity != null ? (
            <p className={css.quantitySummary}>
              <FormattedMessage
                id="OrderPanel.availableQuantitySummary"
                values={{ quantity: availableOrderQuantity }}
              />
            </p>
          ) : null}
          {Number.isInteger(selectedOrderQuantity) ? (
            <p className={css.quantitySummary}>
              <FormattedMessage
                id="OrderPanel.quantitySummary"
                values={{ quantity: selectedOrderQuantity }}
              />
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={css.bookingSec}>
        {auctionEnabled && isListingPage ? (
          <div className={css.timeRemainingSec}>
            <div className={css.time}>
              <span>
                <FormattedMessage id="OrderPanel.timeRemaining" defaultMessage="Time Remaining" />
              </span>
              <span className={css.endsLabel}>
                {countdown.isEnded ? (
                  <FormattedMessage id="OrderPanel.ended" defaultMessage="Ended" />
                ) : (
                  <FormattedMessage
                    id="OrderPanel.endsOn"
                    defaultMessage="Ends {dateLabel}"
                    values={{ dateLabel: endsLabel || '-' }}
                  />
                )}
              </span>
            </div>

            {!countdown.isEnded && isListingPage ? (
              <div className={css.timerSec}>
                <div className={css.timeCount}>
                  <span className={css.count}>{pad2(countdown.days)}</span>
                  <span>
                    <FormattedMessage id="OrderPanel.days" defaultMessage="Days" />
                  </span>
                </div>
                <span className={css.separator}>:</span>
                <div className={css.timeCount}>
                  <span className={css.count}>{pad2(countdown.hrs)}</span>
                  <span>
                    <FormattedMessage id="OrderPanel.hrs" defaultMessage="HRS" />
                  </span>
                </div>
                <span className={css.separator}>:</span>
                <div className={css.timeCount}>
                  <span className={css.count}>{pad2(countdown.mins)}</span>
                  <span>
                    <FormattedMessage id="OrderPanel.mins" defaultMessage="Mins" />
                  </span>
                </div>
                <span className={css.separator}>:</span>
                <div className={css.timeCount}>
                  <span className={css.count}>{pad2(countdown.secs)}</span>
                  <span>
                    <FormattedMessage id="OrderPanel.secs" defaultMessage="Secs" />
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {auctionEnabled && isListingPage ? (
          <div className={css.currentHighestBidSec}>
            <span>
              <FormattedMessage
                id="OrderPanel.currentHighestBid"
                defaultMessage="Current Highest Bid"
              />
            </span>

            <h2>
              {highestBidMoney ? (
                <>
                  <PriceDisplay value={formatMoneyIfSupportedCurrency(highestBidMoney, intl)} />{' '}
                  <sub>{highestBidMoney.currency}</sub>
                </>
              ) : (
                <FormattedMessage id="OrderPanel.noBids" defaultMessage="No bids yet" />
              )}
            </h2>

            <p>
              <Icons name="checkListIcon" />{' '}
              {reserveMoney ? (
                reserveMet ? (
                  <FormattedMessage id="OrderPanel.reserveMet" defaultMessage="Reserve price met" />
                ) : (
                  <FormattedMessage
                    id="OrderPanel.reserveNotMet"
                    defaultMessage="Reserve not met"
                  />
                )
              ) : (
                <FormattedMessage id="OrderPanel.reserveNotSet" defaultMessage="Reserve not set" />
              )}
            </p>
          </div>
        ) : null}

        {auctionEnabled && isListingPage ? (
          <div className={css.placeBidSec}>
            <div className={css.price}>
              <PriceDisplay
                value={
                  minimumBidMoney ? formatMoneyIfSupportedCurrency(minimumBidMoney, intl) : '--'
                }
              />
            </div>

            <div className={css.minBidSec}>
              <span>
                <FormattedMessage
                  id="OrderPanel.minimumBid"
                  defaultMessage="Minimum bid: {minBid}"
                  values={{
                    minBid: (
                      <PriceDisplay
                        value={
                          minimumBidMoney
                            ? formatMoneyIfSupportedCurrency(minimumBidMoney, intl)
                            : '--'
                        }
                      />
                    ),
                  }}
                />
              </span>
            </div>

            {/* ✅ UPDATED: open TransactionPage bid modal */}
            <>
              <div className={css.placeBidBtn}>
                <PrimaryButton
                  disabled={
                    isBidAlreadyPlaced || !isAuthenticated || isOwnListing || countdown.isEnded
                  }
                  onClick={() => setIsBidModalOpen(!isBidModalOpen)}
                >
                  <FormattedMessage id="OrderPanel.placeBid" defaultMessage="Place Bid" />
                </PrimaryButton>
              </div>
              <div className={css.termsWrapper}>
                <FormattedMessage
                  id="OrderPanel.byBidding"
                  defaultMessage="By bidding, you agree to the"
                />{' '}
                <NamedLink name="TermsOfServicePage">
                  <FormattedMessage id="OrderPanel.termsOfSale" defaultMessage="Terms of Sale" />
                </NamedLink>
              </div>
            </>
          </div>
        ) : null}

        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : showBookingFixedDurationForm ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : !auctionEnabled ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            hideQuantityControls={isEquipmentListing}
            listing={listing}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={openContactModal}
            onQuantityChange={setSelectedOrderQuantity}
            setIsOfferModalOpen={setIsOfferModalOpen}
            shouldHideMakeAnOfferButton={shouldHideMakeAnOfferButton}
            transaction={transaction}
            {...sharedProps}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
          />
        ) : showNegotiationForm && isListingPage ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : showRequestQuoteForm && isListingPage ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </div>

      {hideAuthorInfo ? null : (
        <>
          {!isOwnListing ? (
            <div className={css.supportWrapper}>
              <h2 className={css.supportTitle}>
                <FormattedMessage id="ContactChevalierForm.supportHeading" />
              </h2>
              <p className={css.supportText}>
                <FormattedMessage id="ContactChevalierForm.supportBody" />
              </p>
              <SecondaryButton className={css.supportLink} onClick={openContactModal}>
                <FormattedMessage id="ContactChevalierForm.supportCta" />
              </SecondaryButton>
              {contactSuccess ? (
                <p className={css.supportSuccess}>
                  <FormattedMessage id="ContactChevalierForm.submitSuccess" />
                </p>
              ) : null}
            </div>
          ) : null}
          <div className={css.protectionSec}>
            <div className={css.protectionType}>
              <h2>
                <FormattedMessage
                  id="OrderPanel.buyerProtection.title"
                  defaultMessage="Buyer Protection"
                />
              </h2>
              <p>
                <FormattedMessage
                  id="OrderPanel.buyerProtection.desc"
                  defaultMessage="Funds held in escrow until transfer."
                />
              </p>
            </div>
            <div className={css.protectionType}>
              <h2>
                <FormattedMessage
                  id="OrderPanel.insuredShipping.title"
                  defaultMessage="Insured Shipping"
                />
              </h2>
              <p>
                <FormattedMessage
                  id="OrderPanel.insuredShipping.desc"
                  defaultMessage="Global logistics partners available."
                />
              </p>
            </div>
          </div>
        </>
      )}

      <Modal
        id="OrderPanelContactChevalierModal"
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ContactChevalierForm
          initialValues={contactInitialValues}
          onSubmit={submitContactRequest}
          inProgress={contactInProgress}
          error={contactError}
        />
      </Modal>

      {/* <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            id={ORDER_PANEL_SUBMIT_BUTTON_ID}
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm || showNegotiationForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : showNegotiationForm ? (
              <FormattedMessage
                id="OrderPanel.ctaButtonMessageMakeOffer"
                defaultMessage="Make offer"
              />
            ) : showRequestQuoteForm ? (
              <FormattedMessage
                id="OrderPanel.ctaButtonMessageRequestAQuote"
                defaultMessage="Request a quote"
              />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div> */}
      <Modal
        id="Bid.modal"
        isOpen={isBidModalOpen}
        onClose={() => setIsBidModalOpen(false)}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={intl.formatMessage({
          id: 'OrderPanel.bidModalTitle',
        })}
      >
        <BiddingFormComponent
          onSubmit={values => {
            const { biddingPrice } = values || {};

            handleBidTransaction({
              dispatch,
              bidderId: currentUser?.id?.uuid,
              biddrName: maskCurrentUserName,
              biddingAmount: biddingPrice?.amount,
              biddingCurrency: biddingPrice?.currency,
              biddingTime: moment().unix(),
              listingId: listing?.id,
              processAlias: transactionProcessAlias,
              history,
              routeConfiguration,
              auctionEndUnix: moment(auctionEndTime).unix(),
              setIsBidModalOpen,
            });
          }}
          marketplaceCurrency={marketplaceCurrency}
          updateInProgress={placeBidInProgress}
          listingMinimumPriceSubUnits={price?.amount}
          listingBids={listingBids}
          highestBid={highestBid}
          highestBidCurrency={highestBidCurrency}
          listingQuantity={availableOrderQuantity}
          bidUnitLabels={bidUnitLabels}
        />
      </Modal>
    </div>
  );
};

export default OrderPanel;
